import { Archive, BookOpen, Clock, Copy, EyeOff, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import type { MenuSummaryDto } from "../../../shared/api-types";
import { hoursLines } from "../../../shared/hours";
import { menuApi, useMenus } from "../../api/queries";
import { type MenuItem, Modal, PopMenu, StatusChip } from "../../components/kit";
import { useToast } from "../../components/Toast";
import { Alert, ICON } from "../../components/ui";
import { formatWhen, plural } from "../../lib/format";
import type { ShellContext } from "../AppShell";
import { HoursEditor } from "../settings/HoursEditor";
import { ConfirmDialog, MenuNameDialog } from "./dialogs";
import { menuChip } from "./status";
import { useRefreshMenus } from "./useRefresh";

const DAYS = ["Lun", "Mar", "Mie", "Joi", "Vin", "Sâm", "Dum"];
/** "Lun–Vin 12:00–16:00" for the menu card; only the days it is shown. */
const scheduleText = (s: string) =>
  hoursLines(s, DAYS, "")
    .filter((l) => l.hours)
    .map((l) => `${l.days} ${l.hours}`)
    .join(", ");

type Dialog =
  | { type: "create" }
  | { type: "rename" | "duplicate" | "archive" | "unpublish" | "delete" | "schedule"; menu: MenuSummaryDto }
  | null;

/** "Meniurile mele" (prototype page `menus`). */
export function MenusPage() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const menus = useMenus(rid);
  const api = menuApi(rid);
  const refresh = useRefreshMenus(rid);
  const toast = useToast();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [schedule, setSchedule] = useState("");
  const [scheduleError, setScheduleError] = useState("");
  const [saving, setSaving] = useState(false);
  const close = () => setDialog(null);
  const list = [...(menus.data ?? [])].sort((a, b) => Number(a.archived) - Number(b.archived));

  const actions = (m: MenuSummaryDto): MenuItem[] =>
    m.archived
      ? [
          {
            label: "Scoate din arhivă",
            icon: RefreshCw,
            onSelect: async () => {
              await api.unarchive(m.id);
              await refresh(m.id);
              toast(`„${m.name}” a fost scos din arhivă. Nu este public până îl publici.`);
            },
          },
          ...(m.publishedVersionId
            ? []
            : [
                {
                  label: "Șterge definitiv",
                  icon: Trash2,
                  danger: true,
                  onSelect: () => setDialog({ type: "delete", menu: m }),
                },
              ]),
        ]
      : [
          { label: "Redenumește", icon: Pencil, onSelect: () => setDialog({ type: "rename", menu: m }) },
          {
            label: "Program de afișare",
            icon: Clock,
            onSelect: () => {
              setSchedule(m.schedule);
              setScheduleError("");
              setDialog({ type: "schedule", menu: m });
            },
          },
          { label: "Duplică", icon: Copy, onSelect: () => setDialog({ type: "duplicate", menu: m }) },
          ...(m.publicVisible
            ? [
                {
                  label: "Retrage din public",
                  icon: EyeOff,
                  onSelect: () => setDialog({ type: "unpublish", menu: m }),
                },
              ]
            : []),
          { label: "Arhivează", icon: Archive, onSelect: () => setDialog({ type: "archive", menu: m }) },
          ...(m.publishedVersionId
            ? []
            : [
                {
                  label: "Șterge",
                  icon: Trash2,
                  danger: true,
                  onSelect: () => setDialog({ type: "delete", menu: m }),
                },
              ]),
        ];

  return (
    <>
      <div className="page-head">
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="h1">Meniurile mele</h1>
          <span className="page-sub">
            Toate meniurile active apar la același link public. Arhivatele nu sunt vizibile clienților.
          </span>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setDialog({ type: "create" })}>
          <Plus {...ICON} aria-hidden="true" />
          Meniu nou
        </button>
      </div>

      {menus.isError && (
        <Alert tone="error" live>
          {menus.error.message}
        </Alert>
      )}
      {menus.isLoading && (
        <p className="muted" role="status">
          Se încarcă meniurile…
        </p>
      )}
      {menus.data && list.length === 0 && (
        <div className="empty-card">
          <span
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: "var(--color-selected)",
              color: "var(--color-primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BookOpen size={22} strokeWidth={1.75} aria-hidden="true" />
          </span>
          <span className="t">Încă nu ai niciun meniu</span>
          <span className="s">Importă meniul existent sau creează unul nou, gol.</span>
          <div className="row" style={{ justifyContent: "center", gap: 8 }}>
            <button type="button" className="btn btn-primary" onClick={() => navigate("/app/imports")}>
              Importă meniul
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setDialog({ type: "create" })}>
              Creează un meniu gol
            </button>
          </div>
        </div>
      )}

      <div className="stack" style={{ gap: 12 }}>
        {list.map((m) => {
          const chip = menuChip(m);
          return (
            <div className="menu-row" key={m.id}>
              <div
                style={{ flex: "1 1 240px", minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}
              >
                <div className="row" style={{ gap: 10 }}>
                  <span className="nm">{m.name}</span>
                  <StatusChip {...chip} large />
                </div>
                <span className="page-sub">
                  {plural(m.productCount, "produs", "produse")} ·{" "}
                  {plural(m.categoryCount, "categorie", "categorii")}
                  {m.publishedAt ? ` · publicat ${formatWhen(m.publishedAt)}` : " · niciodată publicat"}
                </span>
                {m.schedule && (
                  <span
                    className="caption muted"
                    style={{ display: "inline-flex", gap: 6, alignItems: "center" }}
                  >
                    <Clock size={14} aria-hidden="true" />
                    Afișat doar: {scheduleText(m.schedule) || "niciodată"}
                  </span>
                )}
              </div>
              <div className="row" style={{ gap: 8, marginLeft: "auto" }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    m.archived
                      ? toast("Scoate meniul din arhivă ca să îl editezi.")
                      : navigate(`/app/products?menu=${m.id}`)
                  }
                >
                  {m.archived ? "Arhivat" : "Deschide"}
                </button>
                <PopMenu label={`Acțiuni pentru ${m.name}`} items={actions(m)} />
              </div>
            </div>
          );
        })}
      </div>

      {dialog?.type === "schedule" && (
        <Modal
          title={`Program de afișare: ${dialog.menu.name}`}
          body="Meniul apare la linkul public doar în intervalele alese (ora restaurantului), de exemplu un meniu de prânz de luni până vineri. În afara lor, clienții văd celelalte meniuri. Schimbarea se aplică imediat."
          onClose={close}
          actions={
            <>
              <button type="button" className="btn btn-secondary" onClick={close}>
                Renunță
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving || schedule === dialog.menu.schedule}
                onClick={async () => {
                  setSaving(true);
                  setScheduleError("");
                  try {
                    await api.setSchedule(dialog.menu.id, schedule);
                    await refresh(dialog.menu.id);
                    toast(
                      schedule
                        ? `„${dialog.menu.name}” apare doar: ${scheduleText(schedule) || "niciodată"}.`
                        : `„${dialog.menu.name}” apare oricând.`,
                    );
                    close();
                  } catch (e) {
                    setScheduleError(e instanceof Error ? e.message : String(e));
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Salvează
              </button>
            </>
          }
        >
          <HoursEditor
            value={schedule}
            onChange={setSchedule}
            error={scheduleError || undefined}
            toggleLabel="Afișează meniul doar în anumite zile și ore"
          />
        </Modal>
      )}
      {dialog?.type === "create" && (
        <MenuNameDialog
          mode="create"
          onClose={close}
          onSubmit={async (name) => {
            const created = await api.create(name);
            await refresh(created.id);
            close();
            toast(`„${name}” a fost creat ca ciornă.`);
            navigate(`/app/products?menu=${created.id}`);
          }}
        />
      )}
      {dialog?.type === "rename" && (
        <MenuNameDialog
          mode="rename"
          initial={dialog.menu.name}
          onClose={close}
          onSubmit={async (name) => {
            await api.rename(dialog.menu.id, name);
            await refresh(dialog.menu.id);
            close();
            toast(`Meniul „${dialog.menu.name}” se numește acum „${name}”.`);
          }}
        />
      )}
      {dialog?.type === "duplicate" && (
        <ConfirmDialog
          danger={false}
          title={`Duplici „${dialog.menu.name}”?`}
          body={`Se creează „${dialog.menu.name} (copie)” ca ciornă nepublicată, cu toate categoriile și produsele din ciorna actuală. Meniul original nu se schimbă.`}
          confirmLabel="Duplică meniul"
          onClose={close}
          onConfirm={async () => {
            const copy = await api.duplicate(dialog.menu.id);
            await refresh(copy.id);
            close();
            toast(`„${copy.name}” a fost creat ca ciornă.`);
          }}
        />
      )}
      {dialog?.type === "archive" && (
        <ConfirmDialog
          title={`Arhivezi „${dialog.menu.name}”?`}
          body={
            dialog.menu.publicVisible
              ? "Meniul dispare din meniul public imediat. Îl poți scoate din arhivă oricând; datele rămân."
              : "Meniul nu este public. Îl poți scoate din arhivă oricând; datele rămân."
          }
          confirmLabel="Arhivează"
          onClose={close}
          onConfirm={async () => {
            await api.archive(dialog.menu.id);
            await refresh(dialog.menu.id);
            close();
            toast(`„${dialog.menu.name}” a fost arhivat.`);
          }}
        />
      )}
      {dialog?.type === "unpublish" && (
        <ConfirmDialog
          title={`Retragi „${dialog.menu.name}” din public?`}
          body="Clienții care scanează codul QR nu vor mai vedea acest meniu. Ciorna și versiunea publicată rămân salvate; îl poți publica din nou la același link."
          confirmLabel="Retrage din public"
          onClose={close}
          onConfirm={async () => {
            await api.unpublish(dialog.menu.id);
            await refresh(dialog.menu.id);
            close();
            toast(`„${dialog.menu.name}” nu mai este vizibil clienților.`);
          }}
        />
      )}
      {dialog?.type === "delete" && (
        <ConfirmDialog
          title={`Ștergi definitiv „${dialog.menu.name}”?`}
          body={`Se șterg ${plural(dialog.menu.productCount, "produs", "produse")} și ${plural(dialog.menu.categoryCount, "categorie", "categorii")}. Acțiunea nu poate fi anulată.`}
          confirmLabel="Șterge definitiv"
          onClose={close}
          onConfirm={async () => {
            await api.remove(dialog.menu.id);
            await refresh();
            close();
            toast(`„${dialog.menu.name}” a fost șters.`);
          }}
        />
      )}
    </>
  );
}
